import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { generateOffer, listWorkspaces } from "@/lib/api";
import { TEMPLATES } from "@/lib/templates";
import { Lightning, ArrowLeft, Sparkle, UsersThree } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import Footer from "@/components/Footer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const fieldClass =
  "bg-transparent border-0 border-b border-white/15 focus-visible:border-[#C9A961] focus-visible:ring-0 text-[#F5F1E8] placeholder:text-white/40 rounded-none px-0 py-3 transition-colors";

const EMPTY = {
  current_offer: "",
  target_customer: "",
  current_price: "",
  pain_points: "",
  dream_outcome: "",
  industry: "",
};

export default function NewOffer() {
  const { user, loading, refresh, appConfig } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  // Never hard-code the price here: the server decides it (it depends on which
  // engine is switched on) and it is the thing that actually debits.
  const generateCost = Number(appConfig?.generate_credit_cost ?? 1);
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceId, setWorkspaceId] = useState("");
  const showPricing = appConfig?.show_pricing ?? false;

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      listWorkspaces()
        .then(setWorkspaces)
        .catch(() => {});
    }
  }, [user]);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const applyTemplate = (tpl) => {
    setForm({ ...EMPTY, ...tpl.data });
    toast.success(`Loaded "${tpl.label}" template`);
  };

  const canSubmit =
    form.current_offer.trim() &&
    form.target_customer.trim() &&
    form.current_price.trim() &&
    form.pain_points.trim();

  const totalCredits =
    (user?.credits ?? 0) + (user && !user.has_used_free_credit ? 1 : 0);
  const isAdmin = !!user?.is_admin;
  const outOfCredits = user && !isAdmin && totalCredits === 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    if (outOfCredits) {
      if (showPricing) {
        navigate("/pricing");
      } else {
        toast.error("You're out of credits. Please contact support to get more.");
      }
      return;
    }
    setSubmitting(true);
    try {
      const offer = await generateOffer(form, workspaceId || null);
      toast.success("Irresistible Offer forged");
      await refresh();
      navigate(`/offer/${offer.id}`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        toast.error("Out of credits");
        if (showPricing) navigate("/pricing");
        return;
      }
      console.error(err);
      toast.error(err?.response?.data?.detail || "Forge failed. Try refining inputs.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-2 border-[#C9A961] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1628] text-[#F5F1E8] flex flex-col">
      <Navbar />
      <Toaster theme="dark" position="top-right" />

      <div className="flex-1 max-w-5xl mx-auto w-full px-6 md:px-10 py-12 md:py-16">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-[#9AA5B5] hover:text-white inline-flex items-center gap-2 mb-10"
          data-testid="new-offer-back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-sm tracking-[0.3em] uppercase font-bold text-[#C9A961] mb-3">
            Step 01 — The raw material
          </p>
          <h1 className="font-display text-4xl md:text-5xl font-light tracking-tighter leading-none">
            Drop in your <span className="font-black">current offer</span>.
          </h1>
          <p className="mt-4 text-[#9AA5B5] max-w-2xl">
            Be specific. The more honest detail you give, the more devastating
            the Irresistible Offer we forge back.
          </p>
        </motion.div>

        {outOfCredits && (
          <div
            className="mt-10 bg-gradient-to-r from-[#0F1F38] to-[#0F1F38] border border-[#C9A961]/40 rounded-lg p-6 flex flex-wrap items-center justify-between gap-4"
            data-testid="out-of-credits-banner"
          >
            <div>
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#C9A961] mb-1">
                Out of credits
              </p>
              <p className="text-sm text-[#F5F1E8]">
                {showPricing
                  ? "You've used your free generation. Top up with a credit pack to keep forging."
                  : "You've used your free-launch allotment. Reach out to your account contact to request more."}
              </p>
            </div>
            {showPricing && (
              <Button
                onClick={() => navigate("/pricing")}
                className="bg-[#C9A961] text-[#0A1628] font-bold hover:bg-[#E0B64B] rounded-sm px-6"
                data-testid="out-of-credits-buy-btn"
              >
                <Lightning weight="fill" className="mr-2 h-4 w-4" />
                Buy credits
              </Button>
            )}
          </div>
        )}

        {/* Workspace selector (if user has any) */}
        {workspaces.length > 0 && (
          <div className="mt-10 bg-[#0F1F38] border border-white/5 rounded-lg p-5 flex flex-wrap items-center gap-4" data-testid="workspace-selector-row">
            <UsersThree weight="duotone" className="h-5 w-5 text-[#C9A961] shrink-0" />
            <div className="flex-1 min-w-[200px]">
              <div className="text-xs tracking-[0.25em] uppercase font-bold text-[#C9A961] mb-1">
                Save to
              </div>
              <Select
                value={workspaceId || "personal"}
                onValueChange={(v) => setWorkspaceId(v === "personal" ? "" : v)}
              >
                <SelectTrigger
                  className="bg-[#0A1628] border-white/15 text-[#F5F1E8]"
                  data-testid="workspace-select-trigger"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0F1F38] border-white/15 text-[#F5F1E8]">
                  <SelectItem value="personal">My private vault</SelectItem>
                  {workspaces.map((ws) => (
                    <SelectItem key={ws.workspace_id} value={ws.workspace_id}>
                      {ws.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Templates */}
        <div className="mt-12">
          <div className="flex items-center gap-2 mb-4">
            <Sparkle weight="fill" className="h-4 w-4 text-[#C9A961]" />
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#C9A961]">
              Start from a template
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="template-row">
            {TEMPLATES.map((tpl) => (
              <button
                key={tpl.id}
                type="button"
                onClick={() => applyTemplate(tpl)}
                className="group text-left px-4 py-3 rounded-sm bg-[#0F1F38] border border-white/5 hover:border-[#C9A961]/40 transition-all"
                data-testid={`template-${tpl.id}`}
              >
                <div className="text-sm font-bold text-[#F5F1E8] group-hover:text-white">
                  {tpl.label}
                </div>
                <div className="text-xs text-[#9AA5B5]">{tpl.sub}</div>
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-12 space-y-12" data-testid="new-offer-form">
          <div className="grid grid-cols-12 gap-x-8 gap-y-12">
            <div className="col-span-12">
              <Label className="text-xs tracking-[0.25em] uppercase font-bold text-[#C9A961]">
                Current offer*
              </Label>
              <Textarea
                value={form.current_offer}
                onChange={(e) => update("current_offer", e.target.value)}
                placeholder="e.g. We run Facebook & Instagram ads for e-commerce brands. $2,500/mo retainer + 10% of ad spend."
                rows={4}
                className={fieldClass + " resize-none"}
                data-testid="input-current-offer"
              />
            </div>

            <div className="col-span-12 md:col-span-7">
              <Label className="text-xs tracking-[0.25em] uppercase font-bold text-[#C9A961]">
                Target customer*
              </Label>
              <Input
                value={form.target_customer}
                onChange={(e) => update("target_customer", e.target.value)}
                placeholder="e.g. DTC e-commerce founders doing $50K–$500K/mo"
                className={fieldClass}
                data-testid="input-target-customer"
              />
            </div>

            <div className="col-span-12 md:col-span-5">
              <Label className="text-xs tracking-[0.25em] uppercase font-bold text-[#C9A961]">
                Current price*
              </Label>
              <Input
                value={form.current_price}
                onChange={(e) => update("current_price", e.target.value)}
                placeholder="e.g. $2,500/mo + 10% ad spend"
                className={fieldClass}
                data-testid="input-current-price"
              />
            </div>

            <div className="col-span-12">
              <Label className="text-xs tracking-[0.25em] uppercase font-bold text-[#C9A961]">
                Pain points & objections*
              </Label>
              <Textarea
                value={form.pain_points}
                onChange={(e) => update("pain_points", e.target.value)}
                placeholder="e.g. Burned by 'expert' agencies, hate long retainers, vague reporting, want ROI proof first."
                rows={3}
                className={fieldClass + " resize-none"}
                data-testid="input-pain-points"
              />
            </div>

            <div className="col-span-12 md:col-span-7">
              <Label className="text-xs tracking-[0.25em] uppercase font-bold text-[#9AA5B5]">
                Dream outcome (optional)
              </Label>
              <Textarea
                value={form.dream_outcome}
                onChange={(e) => update("dream_outcome", e.target.value)}
                placeholder="e.g. 3x ROAS on $100K/mo ad spend in 90 days."
                rows={2}
                className={fieldClass + " resize-none"}
                data-testid="input-dream-outcome"
              />
            </div>

            <div className="col-span-12 md:col-span-5">
              <Label className="text-xs tracking-[0.25em] uppercase font-bold text-[#9AA5B5]">
                Industry (optional)
              </Label>
              <Input
                value={form.industry}
                onChange={(e) => update("industry", e.target.value)}
                placeholder="e.g. E-commerce, B2B SaaS"
                className={fieldClass}
                data-testid="input-industry"
              />
            </div>
          </div>

          <div className="pt-8 border-t border-white/5 flex flex-wrap items-center justify-between gap-4">
            <p className="text-xs text-[#808A9B] uppercase tracking-[0.2em]">
              Generation usually takes 20–60 seconds ·{" "}
              <span data-testid="generate-credit-cost">
                {generateCost} credit{generateCost === 1 ? "" : "s"}
              </span>
            </p>
            <Button
              type="submit"
              disabled={!canSubmit || submitting}
              className="bg-[#C9A961] text-[#0A1628] font-bold hover:bg-[#E0B64B] rounded-sm px-8 py-6 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="submit-forge-btn"
            >
              {submitting ? (
                <>
                  <div className="mr-2 h-4 w-4 rounded-full border-2 border-black border-t-transparent animate-spin" />
                  Forging…
                </>
              ) : (
                <>
                  <Lightning weight="fill" className="mr-2 h-5 w-5" />
                  {outOfCredits ? "Buy credits to forge" : "Forge my Irresistible Offer"}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
}
